import { useCallback, useEffect, useRef, useState } from "react";

const DEFAULT_TTL = 5 * 60 * 1000;

const store = new Map();
const inflight = new Map();

export function getCached(key, ttl = DEFAULT_TTL) {
  const hit = store.get(key);
  if (hit && Date.now() - hit.timestamp < ttl) {
    return hit.data;
  }
  return undefined;
}

export async function cachedCall(key, fetcher, ttl = DEFAULT_TTL) {
  const cached = getCached(key, ttl);
  if (cached !== undefined) {
    return cached;
  }

  if (!inflight.has(key)) {
    const request = (async () => {
      try {
        const result = await fetcher();
        if (result !== null && result !== undefined) {
          store.set(key, { data: result, timestamp: Date.now() });
        }
        return result;
      } finally {
        inflight.delete(key);
      }
    })();
    inflight.set(key, request);
  }

  return inflight.get(key);
}

export function invalidateCache(prefix) {
  Array.from(store.keys())
    .filter((key) => !prefix || key.startsWith(prefix))
    .forEach((key) => store.delete(key));
}

export function useCachedResource(key, fetcher, ttl = DEFAULT_TTL) {
  const [data, setData] = useState(() => getCached(key, ttl));
  const [loading, setLoading] = useState(data === undefined);
  const mountedRef = useRef(true);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(
    async (force) => {
      if (force) {
        store.delete(key);
      } else {
        const cached = getCached(key, ttl);
        if (cached !== undefined) {
          setData(cached);
          setLoading(false);
          return cached;
        }
      }
      setLoading(true);
      const result = await cachedCall(key, fetcherRef.current, ttl);
      if (mountedRef.current && result !== null && result !== undefined) {
        setData(result);
      }
      setLoading(false);
      return result;
    },
    [key, ttl]
  );

  useEffect(() => {
    load(false);
  }, [load]);

  return { data, loading, refresh: () => load(true) };
}
